/**
 * <FilterParamsDisplay />
 *
 * Renders a row of chips for every currently-active filter value.
 * Handles all FilterValue types:
 *   - select / text / date / number  — one chip per section
 *   - multiSelect                    — one chip per value in the array
 *   - boolean                        — one chip showing Yes / No (or option label)
 *
 * Returns null when there are no active filters so it takes no space.
 */

import { X } from 'lucide-react'
import type { FilterSection, FilterState } from '@/types/filter'

interface FilterParamsDisplayProps {
  sections: FilterSection[]
  filterState: FilterState
  onRemove: (key: string, urlValue: string) => void
  onClearAll: () => void
  className?: string
}

interface Chip {
  key: string
  urlValue: string
  sectionLabel: string
  valueLabel: string
}

function resolveLabel(section: FilterSection, urlValue: string): string {
  const type = section.type ?? 'select'
  if (section.options) {
    const match = section.options.find(o => o.value === urlValue)
    if (match) return match.label
  }
  if (type === 'boolean') {
    return urlValue === 'true' ? 'Yes' : 'No'
  }
  return urlValue
}

function buildChips(sections: FilterSection[], filterState: FilterState): Chip[] {
  return sections.flatMap(section => {
    const val = filterState[section.key]
    const type = section.type ?? 'select'
    if (val === null || val === undefined) return []
    if (type === 'multiSelect' && Array.isArray(val)) {
      return (val as string[])
        .filter(v => v !== '')
        .map(v => ({
          key: section.key,
          urlValue: v,
          sectionLabel: section.label,
          valueLabel: resolveLabel(section, v),
        }))
    }
    const urlValue = String(val)
    if (urlValue === '' || urlValue === 'null') return []
    return [{ key: section.key, urlValue, sectionLabel: section.label, valueLabel: resolveLabel(section, urlValue) }]
  })
}

export default function FilterParamsDisplay({
  sections,
  filterState,
  onRemove,
  onClearAll,
  className = '',
}: FilterParamsDisplayProps) {
  const chips = buildChips(sections, filterState)
  if (chips.length === 0) return null
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className="text-sm font-medium  shrink-0 mr-1">Filters:</span>
      {chips.map(chip => (
        <span
          key={`${chip.key}-${chip.urlValue}`}
          className="inline-flex items-center gap-1.5  px-2 py-1 bg-wine-100 border border-wine-200 text-white rounded-md text-sm font-medium"
        >
          <span className="text-sm font-normal">{chip.sectionLabel}:</span>
          <span>{chip.valueLabel}</span>
          <button
            type="button"
            onClick={() => onRemove(chip.key, chip.urlValue)}
            aria-label={`Remove filter ${chip.sectionLabel}: ${chip.valueLabel}`}
            className="rounded-full p-0.5 hover:bg-gray-300 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      ))}
      <button type="button" onClick={onClearAll} className="text-sm font-semibold text-wine-100 hover:underline ml-1 shrink-0">
        Clear all
      </button>
    </div>
  )
}
